import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  ChevronRight,
  ImageOff,
  MapPin,
  Pencil,
  TriangleAlert,
  Users,
} from "lucide-react";
import type { ActivityInfo } from "@/services/api/activity/types";
import { formatDateTime, toImageList } from "@/utils/activityFormat";
import { isTruthyFlag } from "@/utils/businessCoupon";
import { getActivityCategoryLabelKey } from "@/utils/activityCategory";
import ParticipantAvatarStack from "@/components/student/ParticipantAvatarStack";

interface StudentActivityCardProps {
  activity: ActivityInfo;
  showEdit?: boolean;
  /** Route base the card links into, e.g. `/portal/activities`. */
  basePath?: string;
}

const StudentActivityCard: React.FC<StudentActivityCardProps> = ({
  activity,
  showEdit = false,
  basePath = "/student/activities",
}) => {
  const { t } = useTranslation();
  const coverImages = toImageList(activity.cdn_activity_image);
  const dateLabel = formatDateTime(activity.date, activity.time);
  const categoryKey = getActivityCategoryLabelKey(activity.category);
  const categoryLabel = categoryKey ? t(categoryKey) : "";
  const participantCount = Number(activity.participant_count) || 0;
  const capacityCount = Number(activity.max_participants) || 0;
  const isBusinessInactive = isTruthyFlag(activity.is_business_inactive);
  const closedLabel = isTruthyFlag(activity.is_activity_passed)
    ? t("Activity ended")
    : isTruthyFlag(activity.is_deadline_passed)
      ? t("Applications closed")
      : isTruthyFlag(activity.is_full)
        ? t("Full")
        : null;
  const hasFee =
    activity.fee !== null &&
    activity.fee !== undefined &&
    String(activity.fee).trim() !== "";
  const feeLabel = hasFee
    ? Number(activity.fee) === 0
      ? t("Free")
      : `${activity.fee} ${t("HKD")}`
    : "";
  const detailsLink = `${basePath}/${activity.id}`;

  return (
    <article className="group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <Link
        to={detailsLink}
        state={{ activity }}
        className="relative block aspect-[16/9] w-full overflow-hidden bg-gray-50"
      >
        {coverImages.length > 0 ? (
          <img
            src={coverImages[0]}
            alt={activity.title || t("Activities")}
            loading="lazy"
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#fff3e0] to-gray-100">
            <ImageOff size={30} className="text-[#ffa217]/60" />
          </div>
        )}
        {isBusinessInactive && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-50/95 px-2.5 py-1 text-[11px] font-bold text-amber-700 shadow-sm">
            <TriangleAlert size={12} className="shrink-0" />
            {t("Organizer unavailable")}
          </span>
        )}
        {closedLabel && (
          <span
            className={`absolute left-3 inline-flex items-center gap-1 rounded-full bg-gray-900/70 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm ${
              isBusinessInactive ? "top-12" : "top-3"
            }`}
          >
            {closedLabel}
          </span>
        )}
        {feeLabel && (
          <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-gray-800 shadow-sm backdrop-blur">
            {feeLabel}
          </span>
        )}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col p-4">
        <Link
          to={detailsLink}
          state={{ activity }}
          className="min-w-0 outline-none"
        >
          <h3 className="line-clamp-2 text-[15px] font-bold leading-5 text-gray-900 transition group-hover:text-[#ffa217]">
            {activity.title || t("Untitled Event")}
          </h3>
        </Link>

        {categoryLabel && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-[#fff3e0] px-2 py-0.5 text-[10px] font-semibold text-[#ffa217]">
              {categoryLabel}
            </span>
          </div>
        )}

        {activity.description && (
          <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-500">
            {activity.description}
          </p>
        )}

        <div className="mt-3 space-y-1.5 text-xs text-gray-500">
          {dateLabel && (
            <span className="flex min-w-0 items-center gap-1.5">
              <CalendarDays size={13} className="shrink-0 text-[#ffa217]" />
              <span className="truncate">{dateLabel}</span>
            </span>
          )}
          {activity.location && (
            <span className="flex min-w-0 items-center gap-1.5">
              <MapPin size={13} className="shrink-0 text-[#ffa217]" />
              <span className="truncate">{activity.location}</span>
            </span>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <div className="flex min-w-0 items-center gap-2">
            {participantCount > 0 ? (
              <>
                <ParticipantAvatarStack
                  icons={activity.participant_icons}
                  names={activity.participant_names}
                  count={participantCount}
                  maxVisible={5}
                  size={22}
                />
                <span className="truncate text-xs font-semibold text-gray-500">
                  {participantCount}
                  {capacityCount > 0 ? ` / ${capacityCount}` : ""}
                </span>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400">
                <Users size={14} className="text-[#ffa217]" />
                {capacityCount > 0 ? `0 / ${capacityCount}` : t("No participants yet")}
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {showEdit && (
              <Link
                to={`${basePath}/${activity.id}/edit`}
                state={{ activity }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
              >
                <Pencil size={13} />
                {t("Edit")}
              </Link>
            )}
            <Link
              to={detailsLink}
              state={{ activity }}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#ffa217] transition hover:text-[#e08f0e]"
            >
              {t("View details")}
              <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

export default StudentActivityCard;
